import * as React from 'react'

/**
 * Is the Patchbay API reachable from this browser? Set by the API client on every response. A network failure,
 * a request timeout, or 502/504 from Caddy starts a grace period; every other HTTP answer clears
 * it. The shell is amber after 3 s and only becomes red/read-only after 10 s (scope §5.0 States).
 */
export interface ApiHealth {
  reachable: boolean
  /** A failed request gets a grace period: amber after 3 s, red after 10 s. */
  state: 'connected' | 'reconnecting' | 'disconnected'
  /** When the current outage started (first failure since the last success). */
  since: Date | null
}

export const RECONNECTING_AFTER_MS = 3_000
export const DISCONNECTED_AFTER_MS = 10_000

const CONNECTED: ApiHealth = { reachable: true, state: 'connected', since: null }
let state: ApiHealth = CONNECTED
const listeners = new Set<() => void>()
let failureSince: Date | null = null
let reconnectingTimer: ReturnType<typeof setTimeout> | null = null
let disconnectedTimer: ReturnType<typeof setTimeout> | null = null

function emit(next: ApiHealth) {
  state = next
  for (const l of listeners) l()
}

function clearFailure() {
  if (reconnectingTimer) clearTimeout(reconnectingTimer)
  if (disconnectedTimer) clearTimeout(disconnectedTimer)
  reconnectingTimer = null
  disconnectedTimer = null
  failureSince = null
}

function showReconnecting() {
  if (!failureSince) return
  emit({ reachable: true, state: 'reconnecting', since: failureSince })
}

function showDisconnected() {
  if (!failureSince) return
  emit({ reachable: false, state: 'disconnected', since: failureSince })
}

export const apiHealth = {
  get: (): ApiHealth => state,
  markDown(at: Date = new Date()): void {
    if (failureSince) return
    failureSince = at
    const elapsed = Math.max(0, Date.now() - at.getTime())
    if (elapsed >= RECONNECTING_AFTER_MS) showReconnecting()
    else reconnectingTimer = setTimeout(showReconnecting, RECONNECTING_AFTER_MS - elapsed)
    if (elapsed >= DISCONNECTED_AFTER_MS) showDisconnected()
    else disconnectedTimer = setTimeout(showDisconnected, DISCONNECTED_AFTER_MS - elapsed)
  },
  markUp(): void {
    if (!failureSince && state.state === 'connected') return
    clearFailure()
    emit(CONNECTED)
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },
  /** Tests only. */
  reset(): void {
    clearFailure()
    emit(CONNECTED)
  },
}

export function useApiHealth(): ApiHealth {
  return React.useSyncExternalStore(apiHealth.subscribe, apiHealth.get, apiHealth.get)
}

/** "10:42" in the viewer's locale, for "unreachable since 10:42". */
export function clockTime(d: Date): string {
  return d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false })
}
